<script lang="ts">
  import type { FliteImage, FliteVideo } from "$lib/gql/genCms/codegen";
  import { CarouselSize } from "$lib/gql/genCms/codegen";
  import { Position, MediaType } from "$lib/gql/genCms/codegen";
  import type { CardConnection } from "$lib/gql/genCms/codegen";
  import Column from "$lib/components/Column/Column.svelte";
  import { destinationFeatureImgConfig } from "$lib/components/DestinationFeature/DestinationFeature.image-config";
  import { onMount } from "svelte";
  import Carousel from "$lib/components/Carousel/Carousel.svelte";
  import Container from "$lib/components/Container/Container.svelte";
  import DestinationFeatureInner from "$lib/components/DestinationFeature/DestinationFeatureInner.svelte";

  export let mediaType: MediaType = MediaType.Image;
  export let isFirstBlock: boolean = false;
  export let image: FliteImage = {};
  export let imageSm: FliteImage = {};
  export let imageAlt: string = "";
  export let id: string;
  export let title: string;
  export let subtitle: string;
  export let body: string;
  export let ctaOneText: string;
  export let ctaOneLink: string;
  export let ctaTwoText: string;
  export let ctaTwoLink: string;
  export let ctaOneBgColour: string;
  export let ctaTwoBgColour: string;
  export let ctaOneTextLight = false;
  export let ctaTwoTextLight = false;
  export let invert = false;
  export let headingElement = "h1";
  export let video: FliteVideo = {};
  export let videoSm: FliteVideo = {};
  export let cards: CardConnection;
  export let url: string;
  export let blockCtaText: string = "";
  export let blockCtaLink: string = "";
  export let rectangleDivider = false;

  let viewportWidth: number;
  let isMobile = false;
  let mdWidth = 768;
  let isInView: boolean;

  onMount(() => {
    const bodyStyle = getComputedStyle(document.body);
    mdWidth = Number(bodyStyle.getPropertyValue("--md").replace(/\D/g, ""));
  });

  $: isMobile = viewportWidth < mdWidth;
  $: isNotDesktop = viewportWidth <= mdWidth;

  $: if (isNotDesktop) {
    isInView = false;
  } else {
    isInView = true; // disable isInView carousel transition for Desktop
  }
</script>

<style lang="scss">
  .destination-feature {
    contain: paint;
    border-bottom: 1px solid $background;

    @include retina {
      border-bottom: 0.5px solid $background;
    }

    :global(.media-container) {
      @include breakpoint(md) {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 86.25vw;
      }

      @include breakpoint(lg) {
        height: 67vw;
      }

      @include breakpoint(xl) {
        height: 66vw;
      }

      @include breakpoint(xxl) {
        height: 66.5vw;
      }
    }

    :global(.media-container > div) {
      @include breakpoint(md) {
        position: sticky;
        top: 0;
      }
    }

    :global(.content-wrapper--absolute) {
      @include breakpoint(md) {
        position: relative;
        top: auto;
        left: auto;
        right: auto;
        bottom: auto;
      }
    }

    :global(.wrapper) {
      @include breakpoint(md) {
        height: 44.9vw;
      }

      @include breakpoint(lg) {
        height: 45.7vw;
      }

      @include breakpoint(xl) {
        height: 45.95vw;
      }

      @include breakpoint(xxl) {
        height: 46.1vw;
      }
    }
  }

  .card-wrapper {
    position: relative;
    margin-top: -32px;
    z-index: 100;

    @include breakpoint(md) {
      margin-top: -8%;
    }

    @include breakpoint(lg) {
      margin-top: -16%;
    }
  }
</style>

<svelte:window bind:innerWidth={viewportWidth} />

<div class="destination-feature" data-cs-override-id="destination-feature">
  <DestinationFeatureInner
    fluid={true}
    {mediaType}
    {image}
    {imageSm}
    {imageAlt}
    {title}
    {subtitle}
    {body}
    {ctaOneLink}
    {ctaOneText}
    {ctaTwoLink}
    {ctaTwoText}
    {invert}
    {headingElement}
    {videoSm}
    {video}
    position={Position.Center}
    imageConfig={destinationFeatureImgConfig}
    displayDirection={"absolute"}
    {blockCtaText}
    {blockCtaLink}
    {isFirstBlock}
    useAltFont={true}
    headingVariant="display1"
    borderBottom={false}
    {rectangleDivider}
    {ctaOneBgColour}
    {ctaTwoBgColour}
    {ctaOneTextLight}
    {ctaTwoTextLight}
  >
    <svelte:fragment slot="cards">
      {#if !isMobile && cards && cards.edges[0] !== null && cards.edges.length >= 1}
        <Column smSpan={4} mdSpan={8} lgSpan={12} xlSpan={12} xxlSpan={12}>
          <div class="card-wrapper">
            <Carousel
              {id}
              {url}
              title=""
              cta={null}
              size={CarouselSize.Three}
              {cards}
              controls={false}
              withPadding={false}
              twoCardsSm={true}
              expandSpaceBetween={true}
              {isInView}
              borderBottom={false}
            />
          </div>
        </Column>
      {/if}
    </svelte:fragment>
  </DestinationFeatureInner>

  {#if isMobile && cards && cards.edges[0] !== null && cards.edges.length >= 1}
    <Container>
      <Column smSpan={4} mdSpan={8} lgSpan={12} xlSpan={12} xxlSpan={12}>
        <div class="card-wrapper">
          <Carousel
            {id}
            {url}
            title=""
            cta={null}
            size={CarouselSize.Three}
            {cards}
            controls={false}
            withPadding={false}
            twoCardsSm={true}
            expandSpaceBetween={true}
            {isInView}
            borderBottom={false}
          />
        </div>
      </Column>
    </Container>
  {/if}
</div>
